/**
 * The document domain's isomorphic face (ADR 0014).
 *
 * Every file outside `src/document/` reaches this domain through this module. Nothing below it —
 * `document/schema/schema`, `document/schema/index` — is a path another domain may name, which is
 * Rule D. Files inside the domain use relative paths instead.
 *
 * **Explicit named exports, never `export *`** (ADR 0014:81-82): a face is an interface, not a bag.
 * The clause is aimed at a failure this repo shipped rather than at tidiness — `storage/index.ts`
 * star-re-exported a `TaskStatus` with a membership disjoint from the one it named explicitly, and
 * the file's `@ts-nocheck` is what compiled it. A name reaches this list because a caller outside
 * the domain imports it; adding the next one is a line.
 *
 * Server-only modules never reach this face: Turbopack does not shake a server-only re-export out
 * of a client graph, so one poisoned re-export breaks the build for every client consumer
 * (ADR 0014, measured). A `'use server'` action module is a different thing and does reach it —
 * its body never enters a client bundle — and `discovery/actions.ts` is the first one here.
 *
 * **The traffic runs both ways, and #375 proved it.** `app/layout.tsx` is a server component and
 * imports this face for `AutomergeProvider`, which pulls every module the face names into the RSC
 * graph. So each module here that calls a React hook opens with `'use client'`: `use-document.ts`,
 * `internal/change-doc.ts`, `discovery/use-migration-status.ts`, `discovery/use-document-url-sync.ts`
 * and `repo/use-sync-status.ts`. Without the directive `pnpm build` fails with seven errors, and
 * nothing before the build says so.
 *
 * At this commit the domain is its schema, its migration, its repo and its view. Step 1's later
 * commits add `operations/`, `discovery/` and `use-document.ts` beneath it, and this list grows to
 * publish them. `document/` is #153's single permitted partial domain, for the life of the
 * `restructure/step-1` branch only.
 *
 * @see CONTEXT.md for what AppDocument, Entry and Link mean
 */

// ============================================================================
// The document
// ============================================================================

export type { AppDocument } from './schema'

// ============================================================================
// Entries
//
// The document owns `Entry` itself. The per-kind metadata shapes and guards are `entries/`
// vocabulary and are published from `@/entries` — step 2 collected them (#369, B).
// ============================================================================

export type {
  Entry,
  EntryType,
  EntryInsert,
  EntryUpdate,
  EntryWithPeople,
  EntryFilterOptions,
} from './schema'

// `ENTRY_TYPES` is the union as a value. `entries/presentation.ts` is the first caller outside the
// domain to need one: a `Record<EntryType, …>` is total at compile time, and this is what checks
// the literal at runtime too (#390).
export { ENTRY_TYPES } from './schema'

// ============================================================================
// Topics, areas, projects and links
// ============================================================================

export type { Topic, TopicRef, TopicInsert, TopicFilterOptions } from './schema'
export type { Area, AreaInsert } from './schema'
export type {
  Project,
  ProjectStatus,
  ProjectInsert,
  ProjectUpdate,
  ProjectFilterOptions,
} from './schema'
export type { Link, LinkInsert, LinkFilterOptions } from './schema'

// ============================================================================
// People
// ============================================================================

export type { PersonRef } from './schema'

// ============================================================================
// The resolved view
//
// Six modules outside the domain read the person graph, and they take these three names between
// them. `resolveLinks` — the primitive all three route through — stays internal on purpose: it is
// the single place a link is walked (ADR 0012, rule 5), and publishing it would invite a caller
// outside the domain to walk one itself.
//
// `useDocumentView()` returns a `DocumentView`, so the type is published with it (#374).
// `resolvePeopleForEntries` stays internal: nothing outside imports it until #290 adopts it.
// ============================================================================

export {
  resolveLinkedPeople,
  calculateLastInteraction,
  getEntriesForPerson,
} from './view'
export type { DocumentView } from './view'
export { useDocumentView, useDocumentStatus } from './use-document'

// ============================================================================
// Writing
//
// `useDocumentOps()` is the write side (ADR 0012): every operation bound to this device's document,
// each call one change transaction. Nothing outside the domain holds a raw change handle any more,
// which is rule 2 — and the applier that does live in `internal/`, where the rule's own grep
// exempts it.
//
// `newId()` is published because minting is a substrate concern: `AppDocument` is multi-writer, so
// whether two devices can mint the same id is a property of the document rather than of the caller.
//
// The operation modules themselves stay internal. A caller reaches an operation through the bound
// object, never by importing `operations/entries` — that is Rule D, and it is also what keeps a
// pure `(doc, …) => T` function from being called with a document nobody is inside a change on.
// ============================================================================

export { useDocumentOps } from './use-document'
export type { DocumentOps } from './use-document'
export { newId } from './ids'
export type {
  EntryChange,
  EntryDraft,
  EntryPatch,
  GenreScope,
  MetadataPatch,
  ProjectChange,
  ProjectDraft,
} from './operations'

// ============================================================================
// Migration
//
// Stamping a document with its schema version, and validating a migrated one.
//
// The two surfaces are the published names, not the functions behind them. `validateMigration`,
// `MigrationValidationResult` and `useMigrationStatus` left this list on step 9 (#414): the
// settings status panel and the prompt banner were their only callers outside the domain, and
// both are now inside it.
//
// `ensureSchemaVersion` itself is no longer published: performing the stamp needs an applier, and
// no caller outside the domain may hold one (ADR 0012 rule 2). `useEnsureSchemaVersion()` is what
// a caller takes instead — the same effect, on this side of the seam.
//
// `isDebugMode` stays internal: `migration/orchestrator.ts` is its only caller, so publishing it
// would put a name on the domain's surface that nothing outside asks for.
// ============================================================================

export { CURRENT_SCHEMA_VERSION } from './migration/orchestrator'
export { useEnsureSchemaVersion } from './use-document'
export { MigrationValidationStatus } from './migration/validation-status'
export { MigrationPromptBanner } from './migration/prompt-banner'

// ============================================================================
// The repo
//
// How the document syncs and persists. `AutomergeProvider` and `storage/adapter.ts` are the only
// callers outside the domain, and they take these three names between them.
//
// `use-sync-status.ts`, `sync-status-indicator.tsx`, `sync-events.ts`, `supabase-sync.ts` and
// `SupabaseNetworkAdapter.ts` stay internal: nothing outside imports them. `SyncStatusIndicator`
// in particular is a finished surface that nothing mounts (#156) — publishing it would make it
// reachable without deciding the product question that ticket owns.
// ============================================================================

export { getRepo } from './repo/repo'
export { decideNewUserAction } from './repo/new-user-detection'
export type { NewUserDecision } from './repo/new-user-detection'
export { ResetStorageButton } from './repo/reset-storage-button'

// ============================================================================
// Discovery
//
// Which document this user has: boot, the URL, and migration status (#375). `AutomergeProvider`
// is mounted by `app/layout.tsx`. `useMigrationStatus` is internal again since step 9 (#414):
// the prompt banner that reads it is now `migration/prompt-banner.tsx`.
//
// `saveAutomergeDocumentUrl` is a `'use server'` action, and re-exporting one here is sound: an
// action's id hashes the defining module path and export name, not the specifier the caller
// writes, so it survives a barrel unchanged (ADR 0014, measured). What must never reach this face
// is a *server-only* module — one importing `next/headers` in its own right — because Turbopack
// does not shake such a re-export out of a client graph. `actions.ts` is `'use server'`, so its
// body and its `@/account/server` import stay out of every client bundle.
//
// `getAutomergeDocumentUrl` and `useDocumentUrlSync` stay internal: their only callers are inside
// `discovery/` itself.
// ============================================================================

export { AutomergeProvider } from './discovery/provider'
export { saveAutomergeDocumentUrl } from './discovery/actions'

// ============================================================================
// Backup
//
// The two settings buttons, and nothing beneath them. `backup.ts` left `lib/` because ADR 0011
// bounds that directory by an import rule and a module writing an `AppDocument` cannot satisfy it;
// on step 9 (#414) the buttons followed it in, and the ten names they took between them —
// `exportBackup`, `validateBackup`, `parseBackupPreview`, `mergeBackup`, `downloadBackup`,
// `BackupCounts`, `BackupData`, `BackupFile`, `MergeResult`, `ValidateResult` — left this face
// with them. Reading or writing a backup is not something another domain does.
//
// `share-doc.ts` stays internal: it writes a *different* document, one `repo.create()` builds
// rather than the `AppDocument`, and it has no production caller today (#376, F20).
// ============================================================================

export { ExportDataButton } from './backup-export-button'
export { ImportDataButton } from './backup-import-button'
